import React, { useCallback, useMemo } from 'react';
import { format } from 'date-fns';
import { BreakpointQueryName, SpacingName, TypographyName } from '@doveit/lego';
import { NextPage } from 'next';
import ArticlesList from '../client/components/articles-list/ArticlesList';
import PageHead from '../client/components/page-head/PageHead';
import AppLayout from '../client/layouts/app-layout/AppLayout';
import { goToPage } from '../client/utils/links';
import { Adv } from '../domain/adv';
import { RELATED_GUIDES_QUERY } from '../client/graphql/queries/guide';
import { AdvFluidType, Article, Target } from '../domain/graphcms';
import { RELATED_NEWS_QUERY } from '../client/graphql/queries/news';
import { mapArticles } from '../client/graphql/mappers/articleMappers';
import { ArticleType } from '../domain/articleType';
import PageSection from '../client/components/page-section/PageSection';
import { getAdvByType } from '../services/advs/advs';
import PageLayout from '../client/layouts/page-layout/PageLayout';
import BottomBarV2 from '../client/templates/common/bottom-bar-v2/BottomBarV2';
import useRefInView from '../client/hooks/useRefInView/useRefInView';
import AdvFluid from '../client/components/adv-fluid/AdvFluid';
import { BANNER_SELL_PROPERTY } from '../client/themes/icons';
import initGraphQLClient from '../client/graphql/initGraphQLClient';
import { withError, withGetServerSideError } from '../client/hocs/with-server-side-error/withServerSideError';
import { importDynamicData } from '../services/importDynamicData';
import * as styles from '../client/templates/author/Author.style';
import { BottomBarData } from '../data/bottom-bars';

export interface AuthorProps {
  SEO: {
    title: string,
    description: string,
  },
  title: string,
  content: {
    title: string,
    articles: Article[],
    type: ArticleType,
  }[],
  adv: Adv,
  bottomBar: BottomBarData,
}

const INTERSECTION_OPTIONS = { threshold: 1 };

export const Author: NextPage<AuthorProps> = ({
  SEO,
  title,
  content,
  adv,
  bottomBar,
}) => {
  const { ref: headerRef, entry: header } = useRefInView(INTERSECTION_OPTIONS);
  const breadcrumbs = useMemo(() => [
    {
      label: 'Autore',
    },
    {
      label: 'Redazione',
    },
  ], []);

  const onBottomBarClick = useCallback(() => {
    goToPage(bottomBar.urlObject);
  }, [bottomBar]);

  return (
    <AppLayout>
      <PageHead
        SEO={SEO}
      />
      <PageLayout>
        <PageLayout.Wrapper>
          <PageLayout.Header>
            <div
              ref={headerRef}
              css={styles.header}
            >
              <h1 css={styles.title}>
                {title}
              </h1>
            </div>
          </PageLayout.Header>
          <PageLayout.Content>
            <PageLayout.Main>
              {content.map(({ title: sectionTitle, articles, type }, i) => (
                <PageSection padding={i === 0 ? [] : [SpacingName.BIG, 0, 0]} key={type}>
                  <PageSection.Title size={TypographyName.H3}>
                    {sectionTitle}
                  </PageSection.Title>
                  <PageSection.Block>
                    <ArticlesList
                      articles={mapArticles(articles, type)}
                      data-ref={`article-list-${type.toLowerCase()}`}
                    />
                  </PageSection.Block>
                </PageSection>
              ))}
            </PageLayout.Main>
            <PageLayout.Aside>
              <AdvFluid
                title={adv.title}
                description={adv.description}
                cta={adv.cta}
                urlObject={adv.urlObject}
                symbolPath={BANNER_SELL_PROPERTY}
                dataRef="adv-fluid-v1-main-sell-properties-author"
                dataTracking="adv-fluid-v1-main-sell-properties-author"
              />
            </PageLayout.Aside>
          </PageLayout.Content>
          <BottomBarV2
            hiddenFrom={BreakpointQueryName.LG}
            visible={header?.boundingClientRect.y < 0}
            label={bottomBar.label}
            title={bottomBar.title}
            href={bottomBar.urlObject?.as}
            onClick={onBottomBarClick}
          />
          <div css={styles.breadcrumb}>
            <PageLayout.Breadcrumb
              items={breadcrumbs}
            />
          </div>
        </PageLayout.Wrapper>
      </PageLayout>
    </AppLayout>
  );
};

export const getServerSideProps = withGetServerSideError<AuthorProps>(async () => {
  const count = 5;
  const now = new Date();
  const createdAt = now.toISOString();
  const date = format(now, 'yyyy-MM-dd');
  const graphQLClient = initGraphQLClient();

  const [
    pageData,
    adv,
    { data: { older: latestGuides } },
    { data: { older: latestEducational } },
    { data: { older: latestNews } },
  ] = await Promise.all([
    importDynamicData('pages/author.yml'),
    getAdvByType(AdvFluidType.SELL_PROPERTY),
    graphQLClient.query({
      query: RELATED_GUIDES_QUERY,
      variables: {
        count,
        date,
        createdAt,
        target: [
          Target.COMPRATORE,
          Target.VENDITORE,
        ],
      },
    }),
    graphQLClient.query({
      query: RELATED_GUIDES_QUERY,
      variables: {
        count,
        date,
        createdAt,
        target: [
          Target.AGENTE,
        ],
      },
    }),
    graphQLClient.query({
      query: RELATED_NEWS_QUERY,
      variables: {
        count,
        date,
        createdAt,
      },
    }),
  ]);

  return {
    props: {
      ...pageData,
      adv,
      content: [
        {
          title: `Le ultime ${count} guide`,
          articles: latestGuides,
          type: ArticleType.GUIDE,
        },
        {
          title: `Le ultime ${count} notizie`,
          articles: latestNews,
          type: ArticleType.NEWS,
        },
        {
          title: `Gli ultimi ${count} contenuti di formazione`,
          articles: latestEducational,
          type: ArticleType.EDUCATION,
        },
      ],
    },
  };
});

export default withError(Author);
